/**
 * Device Validation for the 4G service product page.
 *
 * Intercepts Add to Cart / Buy Now / Buy on Amazon, shows a popup
 * asking for one Device MAC + Device Model per 4G service, validates via RoboUP backend,
 * and only lets the original action proceed on success.
 *
 * Device Model options are loaded from the RoboUP backend.
 *
 * Config comes from window.DeviceValidationConfig set in the section.
 */
(function () {
  'use strict';

  if (window.__deviceValidationBooted) return;
  window.__deviceValidationBooted = true;

  // ------------------------------------------------------------------
  // CONFIG
  // ------------------------------------------------------------------
  var cfg = (window.DeviceValidationConfig = window.DeviceValidationConfig || {});
  var API_BASE = cfg.apiBase || 'https://www.dft.robot.jszx.wd.roboup.tech';
  var APP_ID = cfg.appId || 'robot';
  var PRIVATE_KEY = cfg.privateKey || '';
  var PROJECT_ID = cfg.projectId || 'a123k8WXwJkkWTj9';
  var SIGNATURE_TIMEOUT_MS = cfg.signatureTimeoutMs || 8000;
  var DEVICE_TIMEOUT_MS = cfg.deviceTimeoutMs || 12000;
  var SUCCESS_FEEDBACK_MS = cfg.successFeedbackMs || 350;
  var SECTION_ID = cfg.sectionId || '';
  var MAIN_PRODUCT_ID = cfg.mainProductId || '';
  var PRODUCT_FORM_ID = cfg.productFormId || '';
  var entryDevicePrefill = readDevicePrefill(window.location.search);

  // Line item property keys (visible in orders).
  var LIP_MAC = 'Device MAC';
  var LIP_MODEL = 'Device Model';
  var SINGLE_PURCHASE_MESSAGE = 'Only one 4G service can be purchased per order.';

  // ------------------------------------------------------------------
  // STATE (in-memory, per page load)
  // ------------------------------------------------------------------
  var state = {
    popup: null,
    modal: null,
    dragHandle: null,
    form: null,
    countText: null,
    devicesEl: null,
    submitBtn: null,
    submitText: null,
    statusEl: null,
    helpDetails: null,
    helpBody: null,
    products: [],
    productsPromise: null,
    deviceRows: [],
    validated: null,   // [{ mac, productKey, productName }]
    pending: null,
    lastFocus: null,
    singlePurchaseToastTimer: null,
    drawerDrag: null,
    scrollLock: null,
    suppressHandleClick: false,
    cartValidationComplete: false,
    checkoutProceeding: false,
    actionReadyObserver: null,
    liveCartItems: null,
    cartRefreshPromise: null,
    cartCheckInProgress: false
  };

  // ------------------------------------------------------------------
  // BOOT
  // ------------------------------------------------------------------
  function boot() {
    state.popup = document.getElementById('DeviceValidationPopup');
    if (!state.popup) return;

    state.form = state.popup.querySelector('[data-dvp-form]');
    state.modal = state.popup.querySelector('.dvp-modal');
    state.dragHandle = state.popup.querySelector('[data-dvp-drag-handle]');
    state.countText = state.popup.querySelector('[data-dvp-count-text]');
    state.devicesEl = state.popup.querySelector('[data-dvp-devices]');
    state.submitBtn = state.popup.querySelector('[data-dvp-submit]');
    state.submitText = state.popup.querySelector('.dvp-submit-text');
    state.statusEl = state.popup.querySelector('[data-dvp-status]');
    state.helpDetails = state.popup.querySelector('[data-dvp-help-details]');
    state.helpBody = state.popup.querySelector('[data-dvp-help-body]');

    renderDeviceRows(1);
    setModelSelectLoading(true);

    state.form.addEventListener('submit', onSubmit);
    state.popup.addEventListener('click', function (e) {
      if (e.target === state.popup) closePopup();
      var closeBtn = e.target.closest && e.target.closest('[data-dvp-close]');
      if (closeBtn) closePopup();
      var dragHandle = e.target.closest && e.target.closest('[data-dvp-drag-handle]');
      if (dragHandle) {
        if (state.suppressHandleClick) {
          state.suppressHandleClick = false;
        } else {
          closePopup();
        }
      }
    });
    if (state.modal) state.modal.addEventListener('pointerdown', onDrawerDragStart);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) closePopup();
    });

    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('submit', onDocumentSubmit, true);
    document.addEventListener('cart-add', onCartChanged);
    document.addEventListener('cart-remove', onCartChanged);
    document.addEventListener('cart-update', onCartChanged);
    state.form.addEventListener('input', onDeviceFormInput);
    state.form.addEventListener('change', onDeviceFormInput);
    initHelpDetailsAnimation();
    markValidationActionsReady();

    prefillFromUrl();
    preloadProductModelsOnProductPage();
  }

  function markValidationActionsReady() {
    window.__deviceValidationReady = true;
    enablePendingValidationActions(document);

    if (!window.MutationObserver || state.actionReadyObserver) return;
    state.actionReadyObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) enablePendingValidationActions(node);
        });
      });
    });
    state.actionReadyObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  function enablePendingValidationActions(root) {
    if (!root) return;

    var actions = [];
    if (root.matches && root.matches('[data-device-validation-pending]')) actions.push(root);
    if (root.querySelectorAll) {
      root.querySelectorAll('[data-device-validation-pending]').forEach(function (action) {
        actions.push(action);
      });
    }

    actions.forEach(function (action) {
      action.removeAttribute('data-device-validation-pending');
      action.removeAttribute('disabled');
      action.removeAttribute('aria-disabled');
    });
  }

  function initHelpDetailsAnimation() {
    if (!state.helpDetails || !state.helpBody) return;

    var summary = state.helpDetails.querySelector('summary');
    if (!summary) return;

    summary.addEventListener('click', function (e) {
      e.preventDefault();
      if (state.helpDetails.classList.contains('is-animating')) return;
      if (state.helpDetails.open) collapseHelpDetails();
      else expandHelpDetails();
    });
  }

  function expandHelpDetails() {
    state.helpDetails.open = true;
    state.helpDetails.classList.add('is-animating', 'is-opening');
    state.helpDetails.classList.remove('is-closing');
    state.helpBody.offsetHeight;
    requestAnimationFrame(function () {
      state.helpDetails.classList.remove('is-opening');
      finishHelpDetailsAnimation(function () {});
    });
  }

  function collapseHelpDetails() {
    state.helpDetails.classList.add('is-animating', 'is-closing');
    finishHelpDetailsAnimation(function () {
      state.helpDetails.open = false;
      state.helpDetails.classList.remove('is-closing');
    });
  }

  function finishHelpDetailsAnimation(callback) {
    var done = false;

    function finish() {
      if (done) return;
      done = true;
      state.helpBody.removeEventListener('transitionend', onTransitionEnd);
      state.helpDetails.classList.remove('is-animating');
      callback();
    }

    function onTransitionEnd(e) {
      if (e.target === state.helpBody && e.propertyName === 'grid-template-rows') finish();
    }

    state.helpBody.addEventListener('transitionend', onTransitionEnd);
    setTimeout(finish, 320);
  }

  function ensureProductModelsLoaded() {
    if (state.products.length) {
      populateModelOptions(state.products);
      prefillFromUrl();
      updateRowStates();
      return Promise.resolve(state.products);
    }

    if (state.productsPromise) return state.productsPromise;

    setModelSelectLoading(true);
    state.productsPromise = loadProductModels()
      .then(function (products) {
        state.products = products;
        populateModelOptions(products);
        prefillFromUrl();
        updateRowStates();
        return products;
      })
      .catch(function (err) {
        console.error('[DeviceValidation] product list error:', err);
        setModelSelectError();
        throw err;
      })
      .finally(function () {
        state.productsPromise = null;
      });

    return state.productsPromise;
  }

  function preloadProductModelsOnProductPage() {
    if (!isDeviceValidationProductPage()) return;

    setTimeout(function () {
      ensureProductModelsLoaded().catch(function () { /* handled when the popup opens */ });
    }, 0);
  }

  function isDeviceValidationProductPage() {
    if (MAIN_PRODUCT_ID && document.getElementById(MAIN_PRODUCT_ID)) return true;
    if (PRODUCT_FORM_ID && document.getElementById(PRODUCT_FORM_ID)) return true;
    return false;
  }

  function prefillFromUrl() {
    var firstRow = state.deviceRows[0];
    if (!firstRow) return;
    if (entryDevicePrefill.mac) firstRow.macInput.value = entryDevicePrefill.mac;
    if (entryDevicePrefill.productKey) {
      selectModelOption(firstRow.modelSelect, entryDevicePrefill.productKey);
    }
  }

  function readDevicePrefill(search) {
    try {
      var qs = new URLSearchParams(search || '');
      return {
        mac: qs.get('mac') || '',
        productKey: qs.get('model') || ''
      };
    } catch (_) {
      return { mac: '', productKey: '' };
    }
  }

  // ------------------------------------------------------------------
  // INTERCEPTION
  // ------------------------------------------------------------------
  function onDocumentClick(e) {
    if (state.popup && state.popup.contains(e.target)) return;

    var checkout = e.target.closest && e.target.closest('[name="checkout"]');
    if (checkout && isCheckoutButton(checkout)) {
      var checkoutForm = getCheckoutForm(checkout);
      if (!checkoutForm || state.checkoutProceeding) return;
      if (cartExceedsSingleDeviceValidationItem()) {
        stop(e);
        showSinglePurchaseMessage();
        return;
      }
      if (!cartRequiresValidation()) return;
      if (typeof checkoutForm.reportValidity === 'function' && !checkoutForm.reportValidity()) return;
      stop(e);
      openPopup({ kind: 'checkout', element: checkout, form: checkoutForm });
      return;
    }

    var addToCart = e.target.closest && e.target.closest('[data-device-validation-action="add-to-cart"], [name="add"], .product-form__submit');
    if (addToCart) {
      var isContractAddToCart = addToCart.getAttribute && addToCart.getAttribute('data-device-validation-action') === 'add-to-cart';
      var isValidationProductAddToCart = requiresDeviceValidation(addToCart);
      if (!isContractAddToCart && !isValidationProductAddToCart && !isTargetActionElement(addToCart)) return;
      var addToCartForm = getProductFormForAction(addToCart);
      if (!addToCartForm) return;
      if (!isContractAddToCart && !isValidationProductAddToCart && !isTargetAddToCartForm(addToCartForm)) return;
      if (typeof addToCartForm.reportValidity === 'function' && !addToCartForm.reportValidity()) return;
      stop(e);
      checkFreshCart(function () {
        if (cartHasDeviceValidationItem()) {
          var existingCartItem = getBindableExistingCartItem(addToCartForm);
          if (existingCartItem) {
            openPopup({ kind: 'bind-cart-item', item: existingCartItem });
            return;
          }
          showSinglePurchaseMessage();
          return;
        }
        openPopup({ kind: 'add-to-cart', element: addToCartForm, submitter: addToCart });
      });
      return;
    }

    var buyNow = e.target.closest && e.target.closest('[data-device-validation-action="buy-now"], .buy_now, .card_buy_now');
    if (buyNow) {
      var isContractBuyNow = buyNow.getAttribute && buyNow.getAttribute('data-device-validation-action') === 'buy-now';
      var isValidationProductBuyNow = requiresDeviceValidation(buyNow);
      if (!isContractBuyNow && !isValidationProductBuyNow && !isTargetActionElement(buyNow)) return;
      var buyNowForm = null;
      if (!isContractBuyNow || buyNow.getAttribute('data-device-validation-form')) {
        buyNowForm = getProductFormForAction(buyNow);
      }
      stop(e);
      checkFreshCart(function () {
        if (cartHasDeviceValidationItem()) {
          showSinglePurchaseMessage();
          return;
        }
        openPopup({ kind: 'buy-now', element: buyNow, form: buyNowForm, submitter: buyNow });
      });
      return;
    }

    var amazon = e.target.closest && e.target.closest('.buy_from_amazon');
    if (amazon) {
      if (!isTargetActionElement(amazon)) return;
      if (isAlreadyValidated(1)) return;
      stop(e);
      openPopup({ kind: 'amazon', element: amazon });
      return;
    }
  }

  function onDocumentSubmit(e) {
    var form = e.target;
    if (isCheckoutForm(form, e.submitter)) {
      if (state.checkoutProceeding) return;
      if (cartExceedsSingleDeviceValidationItem()) {
        stop(e);
        showSinglePurchaseMessage();
        return;
      }
      if (!cartRequiresValidation()) return;
      stop(e);
      openPopup({ kind: 'checkout', element: e.submitter, form: form });
      return;
    }

    if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
    if (!state.popup) return;
    if (!isTargetAddToCartForm(form)) return;

    stop(e);
    checkFreshCart(function () {
      if (cartHasDeviceValidationItem()) {
        var existingCartItem = getBindableExistingCartItem(form);
        if (existingCartItem) {
          openPopup({ kind: 'bind-cart-item', item: existingCartItem });
          return;
        }
        showSinglePurchaseMessage();
        return;
      }
      openPopup({ kind: 'add-to-cart', element: form, submitter: e.submitter });
    });
  }

  function isTargetAddToCartForm(form) {
    if (requiresDeviceValidation(form)) return true;
    if (PRODUCT_FORM_ID) return form.id === PRODUCT_FORM_ID;
    return isTargetActionElement(form);
  }

  function requiresDeviceValidation(element) {
    if (!element || !element.closest) return false;
    var selector = '[data-device-validation-required="true"]';
    if (element.matches && element.matches(selector)) return true;
    return !!element.closest(selector);
  }

  function getProductFormForAction(element) {
    if (!element) return null;

    var contractFormId = element.getAttribute && element.getAttribute('data-device-validation-form');
    if (contractFormId) {
      var contractForm = document.getElementById(contractFormId);
      if (contractForm) return contractForm;
    }

    var form = element.form || null;
    if (!form) {
      var formId = element.getAttribute && element.getAttribute('form');
      if (formId) form = document.getElementById(formId);
    }
    if (form) return form;

    var productForm = element.closest && element.closest('product-form');
    if (productForm) {
      form = productForm.querySelector('form');
      if (form) return form;
    }

    if (PRODUCT_FORM_ID) {
      form = document.getElementById(PRODUCT_FORM_ID);
      if (form) return form;
    }

    var mainProduct = MAIN_PRODUCT_ID ? document.getElementById(MAIN_PRODUCT_ID) : null;
    return mainProduct ? mainProduct.querySelector('form[data-type="add-to-cart-form"]') : null;
  }

  function isTargetActionElement(element) {
    if (!element) return false;
    if (SECTION_ID && element.getAttribute('data-section-id') === SECTION_ID) return true;

    var mainProduct = MAIN_PRODUCT_ID ? document.getElementById(MAIN_PRODUCT_ID) : null;
    if (!mainProduct || !mainProduct.contains(element)) return false;
    if (element.closest && element.closest('cart-drawer, cart-notification, product-recommendations, .quick-add-modal')) return false;
    return true;
  }

  function isCheckoutButton(button) {
    if (!button || button.getAttribute('name') !== 'checkout') return false;
    return !!getCheckoutForm(button);
  }

  function getCheckoutForm(button) {
    if (!button) return null;
    var form = button.form;
    if (!form) {
      var formId = button.getAttribute('form');
      if (formId) form = document.getElementById(formId);
    }
    if (!form) return null;
    return isCartForm(form) ? form : null;
  }

  function isCheckoutForm(form, submitter) {
    if (!isCartForm(form)) return false;
    if (!submitter) return false;
    return submitter.getAttribute && submitter.getAttribute('name') === 'checkout';
  }

  function isCartForm(form) {
    if (!form) return false;
    return form.id === 'CartDrawer-Form' || form.id === 'cart';
  }

  function stop(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }

  // ------------------------------------------------------------------
  // POPUP OPEN / CLOSE
  // ------------------------------------------------------------------
  function isOpen() {
    return state.popup && state.popup.classList.contains('is-active');
  }

  function openPopup(pending) {
    state.pending = pending;
    state.validated = null;
    state.lastFocus = document.activeElement;
    renderDeviceRows(getPendingDeviceCount(pending));
    prefillFromUrl();
    ensureProductModelsLoaded().catch(function () { /* handled by field state */ });
    state.popup.classList.add('is-active');
    state.popup.setAttribute('aria-hidden', 'false');
    lockPageScroll();
    setSuccessState(false);
    clearStatus();
    setTimeout(function () {
      var firstIncomplete = getFirstIncompleteRow();
      if (firstIncomplete && !firstIncomplete.macInput.value) focusWithoutScroll(firstIncomplete.macInput);
      else if (firstIncomplete && !firstIncomplete.modelSelect.value) focusWithoutScroll(firstIncomplete.modelSelect);
      else focusWithoutScroll(state.submitBtn);
    }, 0);
  }

  function closePopup() {
    resetDrawerDragStyles();
    state.popup.classList.remove('is-active');
    state.popup.setAttribute('aria-hidden', 'true');
    setLoading(false);
    setSuccessState(false);
    state.pending = null;
    focusWithoutScroll(state.lastFocus);
    unlockPageScroll();
  }

  function lockPageScroll() {
    if (state.scrollLock) return;

    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    state.scrollLock = {
      scrollY: scrollY,
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      bodyRight: document.body.style.right,
      bodyWidth: document.body.style.width
    };

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockPageScroll() {
    if (!state.scrollLock) return;

    var scrollY = state.scrollLock.scrollY;
    document.body.style.overflow = state.scrollLock.bodyOverflow;
    document.body.style.position = state.scrollLock.bodyPosition;
    document.body.style.top = state.scrollLock.bodyTop;
    document.body.style.left = state.scrollLock.bodyLeft;
    document.body.style.right = state.scrollLock.bodyRight;
    document.body.style.width = state.scrollLock.bodyWidth;
    state.scrollLock = null;

    window.scrollTo(0, scrollY);
  }

  function focusWithoutScroll(element) {
    if (!element || typeof element.focus !== 'function') return;

    try {
      element.focus({ preventScroll: true });
    } catch (_) {
      try { element.focus(); } catch (__) { /* noop */ }
      if (state.scrollLock) window.scrollTo(0, state.scrollLock.scrollY);
    }
  }

  function onDrawerDragStart(e) {
    if (!state.modal || !isOpen()) return;
    if (e.button !== undefined && e.button !== 0) return;
    if (!isDrawerTopDragTarget(e)) return;

    state.drawerDrag = {
      startY: e.clientY,
      lastY: e.clientY,
      maxY: 0,
      startedAt: Date.now()
    };
    state.modal.classList.add('is-dragging');
    state.modal.style.transition = 'none';

    if (state.modal.setPointerCapture) {
      try { state.modal.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
    window.addEventListener('pointermove', onDrawerDragMove);
    window.addEventListener('pointerup', onDrawerDragEnd);
    window.addEventListener('pointercancel', onDrawerDragCancel);
    e.preventDefault();
  }

  function isDrawerTopDragTarget(e) {
    var target = e.target;
    if (target.closest && target.closest('[data-dvp-close], input, select, textarea, [data-dvp-submit]')) return false;
    if (target.closest && target.closest('[data-dvp-drag-handle]')) return true;

    var rect = state.modal.getBoundingClientRect();
    var topDragHeight = Math.min(112, Math.max(64, state.modal.offsetHeight * 0.18));
    return e.clientY >= rect.top && e.clientY <= rect.top + topDragHeight;
  }

  function onDrawerDragMove(e) {
    if (!state.drawerDrag || !state.modal) return;

    var offsetY = Math.max(0, e.clientY - state.drawerDrag.startY);
    state.drawerDrag.lastY = e.clientY;
    state.drawerDrag.maxY = Math.max(state.drawerDrag.maxY, offsetY);
    if (offsetY > 4) state.suppressHandleClick = true;
    state.modal.style.transform = 'translateY(' + offsetY + 'px)';
    e.preventDefault();
  }

  function onDrawerDragEnd(e) {
    if (!state.drawerDrag || !state.modal) return;

    var offsetY = Math.max(0, e.clientY - state.drawerDrag.startY);
    var elapsed = Math.max(Date.now() - state.drawerDrag.startedAt, 1);
    var velocity = offsetY / elapsed;
    var threshold = Math.min(140, state.modal.offsetHeight * 0.28);
    var shouldClose = offsetY > threshold || velocity > 0.8;

    removeDrawerDragListeners();
    state.drawerDrag = null;
    state.modal.classList.remove('is-dragging');

    if (shouldClose) {
      state.modal.style.transition = 'transform 0.18s ease';
      state.modal.style.transform = 'translateY(100%)';
      setTimeout(function () {
        closePopup();
      }, 160);
    } else {
      state.modal.style.transition = 'transform 0.18s ease';
      state.modal.style.transform = '';
      setTimeout(resetDrawerDragStyles, 180);
    }
  }

  function onDrawerDragCancel() {
    if (!state.drawerDrag || !state.modal) return;
    removeDrawerDragListeners();
    state.drawerDrag = null;
    state.modal.classList.remove('is-dragging');
    state.modal.style.transition = 'transform 0.18s ease';
    state.modal.style.transform = '';
    setTimeout(resetDrawerDragStyles, 180);
  }

  function removeDrawerDragListeners() {
    window.removeEventListener('pointermove', onDrawerDragMove);
    window.removeEventListener('pointerup', onDrawerDragEnd);
    window.removeEventListener('pointercancel', onDrawerDragCancel);
  }

  function resetDrawerDragStyles() {
    if (!state.modal) return;
    state.modal.classList.remove('is-dragging');
    state.modal.style.transition = '';
    state.modal.style.transform = '';
    removeDrawerDragListeners();
    state.drawerDrag = null;
  }

  // ------------------------------------------------------------------
  // SUBMIT FLOW
  // ------------------------------------------------------------------
  function onSubmit(e) {
    e.preventDefault();
    clearStatus();

    var collection = collectDeviceRows();
    if (!collection.ok) {
      showRowErrors(collection.errors);
      focusFirstError(collection.errors);
      return;
    }

    setLoading(true);
    validateDevices(collection.devices)
      .then(function (result) {
        setLoading(false);
        if (result.ok) {
          onValidationSuccess(collection.devices);
        } else {
          showRowErrors(result.errors);
          focusFirstError(result.errors);
        }
      })
      .catch(function (err) {
        setLoading(false);
        console.error('[DeviceValidation] error:', err);
        setStatus('Network error, please try again.', 'error');
      });
  }

  function onValidationSuccess(devices) {
    state.validated = devices;
    setSuccessState(true);
    setStatus(devices.length > 1 ? 'Devices verified. Continuing...' : 'Device verified. Continuing...', 'success');

    var pending = state.pending;
    setTimeout(function () {
      closePopup();
      if (!pending) return;

      if (pending.kind === 'add-to-cart') {
        continueAddToCart(pending, devices).catch(function (err) {
          console.error('[DeviceValidation] add to cart error:', err);
          alert('Devices verified, but the cart could not be updated. Please try again.');
        });
      } else if (pending.kind === 'bind-cart-item') {
        continueBindCartItem(pending, devices).catch(function (err) {
          console.error('[DeviceValidation] existing cart item update error:', err);
          alert('Device verified, but the cart could not be updated. Please try again.');
        });
      } else if (pending.kind === 'buy-now') {
        continueBuyNow(pending, devices).catch(function (err) {
          console.error('[DeviceValidation] buy now error:', err);
          alert('Devices verified, but checkout could not continue. Please try again.');
        });
      } else if (pending.kind === 'amazon') {
        var href = pending.element.getAttribute('href');
        var target = pending.element.getAttribute('target');
        if (target === '_blank') {
          window.open(href, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = href;
        }
      } else if (pending.kind === 'checkout') {
        continueCheckout(pending, devices).catch(function (err) {
          console.error('[DeviceValidation] checkout error:', err);
          alert('Device verified, but checkout could not continue. Please try again.');
        });
      }
    }, SUCCESS_FEEDBACK_MS);
  }

  function isAlreadyValidated(expectedCount) {
    if (!state.validated) return false;
    if (expectedCount && state.deviceRows.length !== expectedCount) return false;
    var collection = collectDeviceRows({ silent: true });
    if (!collection.ok) return false;
    if (collection.devices.length !== state.validated.length) return false;
    return collection.devices.every(function (device, index) {
      var validated = state.validated[index];
      return validated && device.mac === validated.mac && device.productKey === validated.productKey;
    });
  }

  function invalidateCache() {
    state.validated = null;
    clearStatus();
    clearRowErrors();
  }

  function onDeviceFormInput(e) {
    if (!e.target || !e.target.matches('[data-dvp-mac], [data-dvp-model]')) return;
    invalidateCache();
    updateRowStates();
  }

  function collectDeviceRows(options) {
    options = options || {};
    var devices = [];
    var errors = [];
    var seenMacs = {};

    for (var i = 0; i < state.deviceRows.length; i++) {
      var row = state.deviceRows[i];
      var rawMac = (row.macInput.value || '').trim();
      var normalizedMac = normalizeMac(rawMac);
      var productKey = row.modelSelect.value;

      if (!normalizedMac) {
        errors.push({
          row: row,
          message: options.silent ? '' : 'Please enter the Device MAC.',
          focusEl: row.macInput
        });
      }

      if (!productKey) {
        errors.push({
          row: row,
          message: options.silent ? '' : 'Please select the Device Model.',
          focusEl: row.modelSelect
        });
      }

      if (normalizedMac && seenMacs[normalizedMac]) {
        errors.push({
          row: row,
          message: options.silent ? '' : 'This MAC address is already used in this order.',
          focusEl: row.macInput
        });
      }
      if (normalizedMac) seenMacs[normalizedMac] = true;

      if (normalizedMac && cartHasMac(normalizedMac)) {
        errors.push({
          row: row,
          message: options.silent ? '' : 'This MAC address is already in your cart.',
          focusEl: row.macInput
        });
      }

      if (normalizedMac && productKey) {
        devices.push({
          row: row,
          mac: normalizedMac,
          productKey: productKey,
          productName: getSelectedModelName(row.modelSelect, productKey)
        });
      }
    }

    if (errors.length) return { ok: false, errors: errors };
    return { ok: true, devices: devices };
  }

  function validateDevices(devices) {
    return devices.reduce(function (promise, device, index) {
      return promise.then(function (result) {
        setStatus('Checking device ' + (index + 1) + ' of ' + devices.length + '...', 'success');
        return validateDevice(device.mac, device.productKey).then(function (validation) {
          if (!validation.ok) {
            result.errors.push({
              row: device.row,
              message: validation.message || 'Device not found or not activated.',
              focusEl: device.row.macInput
            });
          }
          return result;
        });
      });
    }, Promise.resolve({ ok: true, errors: [] })).then(function (result) {
      result.ok = result.errors.length === 0;
      return result;
    });
  }

  function getSelectedModelName(select, fallback) {
    var selectedOption = select && select.options ? select.options[select.selectedIndex] : null;
    return selectedOption ? selectedOption.textContent : fallback;
  }

  function getFirstIncompleteRow() {
    for (var i = 0; i < state.deviceRows.length; i++) {
      var row = state.deviceRows[i];
      if (!row.macInput.value || !row.modelSelect.value) return row;
    }
    return null;
  }

  // ------------------------------------------------------------------
  // LINE ITEM PROPERTIES
  // ------------------------------------------------------------------
  function attachPropertiesToForm(form) {
    if (!form || !state.validated || !state.validated[0]) return;
    setHiddenInput(form, 'properties[' + LIP_MAC + ']', state.validated[0].mac);
    setHiddenInput(form, 'properties[' + LIP_MODEL + ']', state.validated[0].productName);
  }

  function attachPropertiesToBuyNow(buttonEl, device) {
    if (!device) return;
    buttonEl.dataset.dvpMac = device.mac;
    buttonEl.dataset.dvpModel = device.productName;
  }

  function setHiddenInput(form, name, value) {
    var input = form.querySelector('input[type="hidden"][name="' + name.replace(/"/g, '\\"') + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function continueAddToCart(pending, devices) {
    var form = pending && pending.element;
    if (!form) return Promise.resolve();

    return refreshDeviceValidationCartItems().then(function () {
      if (cartHasDeviceValidationItem()) {
        showSinglePurchaseMessage();
        return;
      }

      var items = buildAddToCartItems(form, devices);
      if (!items.length) return Promise.reject(new Error('No cart items to add'));

      return addItemsToCart(form, items).then(function (response) {
        afterAjaxAddToCart(form, response, items);
      });
    });
  }

  function continueBuyNow(pending, devices) {
    var button = pending && pending.element;
    var form = pending && pending.form ? pending.form : getProductFormForAction(button);

    return refreshDeviceValidationCartItems().then(function () {
      if (cartHasDeviceValidationItem()) {
        showSinglePurchaseMessage();
        return;
      }

      var items = form ? buildAddToCartItems(form, devices) : buildButtonCartItems(button, devices);
      if (!items.length) return Promise.reject(new Error('No cart items to add'));

      return addItemsToCart(form, items).then(function () {
        window.location.href = '/checkout';
      });
    });
  }

  function buildAddToCartItems(form, devices) {
    var formData = new FormData(form);
    var id = formData.get('id');
    var sellingPlan = formData.get('selling_plan');
    var baseProperties = getFormProperties(formData);

    return devices.map(function (device) {
      var properties = buildDeviceProperties(baseProperties, device);

      var item = {
        id: Number(id) || id,
        quantity: 1,
        properties: properties
      };
      if (sellingPlan) item.selling_plan = sellingPlan;
      return item;
    });
  }

  function buildButtonCartItems(button, devices) {
    var variantId = button && button.dataset ? button.dataset.variantId : '';
    if (!variantId) return [];

    return devices.map(function (device) {
      return {
        id: Number(variantId) || variantId,
        quantity: 1,
        properties: buildDeviceProperties({}, device)
      };
    });
  }

  function buildDeviceProperties(baseProperties, device) {
    var properties = Object.assign({}, baseProperties || {});
    properties[LIP_MAC] = device.mac;
    properties[LIP_MODEL] = device.productName;
    return properties;
  }

  function getFormProperties(formData) {
    var properties = {};
    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (!match) return;
      if (match[1] === LIP_MAC || match[1] === LIP_MODEL) return;
      if (value === '') return;
      properties[match[1]] = value;
    });
    return properties;
  }

  function addItemsToCart(form, items) {
    var productForm = form && form.closest ? form.closest('product-form') : null;
    var sections = getCartSections(productForm);
    var body = { items: items };

    if (sections.length > 0) {
      body.sections = Array.from(new Set(sections));
      body.sections_url = window.location.pathname;
      setCartActiveElement(productForm);
    }

    var config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' }
    };
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(body);

    return fetch((window.routes && routes.cart_add_url) || '/cart/add.js', config)
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response && response.status) {
          throw new Error(response.description || response.message || 'Cart add failed');
        }
        return response;
      });
  }

  function getCartSections(productForm) {
    var sections = [];
    var cartDrawer = document.querySelector('cart-drawer');
    var cartItems = document.querySelector('cart-items');
    var cartNotification = document.querySelector('cart-notification');

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      sections = sections.concat(cartDrawer.getSectionsToRender().map(function (section) { return section.id; }));
    }
    if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
      if (typeof window.loadTemplateContent === 'function') window.loadTemplateContent(cartNotification);
      sections = sections.concat(cartNotification.getSectionsToRender().map(function (section) { return section.id; }));
    }
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      sections = sections.concat(cartItems.getSectionsToRender().map(function (section) { return section.section; }));
    }

    return sections.filter(Boolean);
  }

  function setCartActiveElement(productForm) {
    var cartDrawer = document.querySelector('cart-drawer');
    var cartNotification = document.querySelector('cart-notification');
    var atcAction = window.theme && theme.settings && theme.settings.cartDrawer ? theme.settings.cartDrawer.atcAction : '';
    if (atcAction === 'drawer' && cartDrawer && typeof cartDrawer.setActiveElement === 'function') {
      cartDrawer.setActiveElement(document.activeElement);
    } else if (atcAction === 'notification' && cartNotification && typeof cartNotification.setActiveElement === 'function') {
      cartNotification.setActiveElement(document.activeElement);
    }
  }

  function afterAjaxAddToCart(form, response, items) {
    var atcAction = window.theme && theme.settings && theme.settings.cartDrawer ? theme.settings.cartDrawer.atcAction : '';
    if (atcAction === 'page') {
      window.location = window.routes.cart_url;
      return;
    }

    var productForm = form.closest ? form.closest('product-form') : null;
    var submitButton = productForm ? productForm.querySelector('[type="submit"]') : null;
    var cartDrawer = document.querySelector('cart-drawer');
    var cartItems = document.querySelector('cart-items');
    var cartNotification = document.querySelector('cart-notification');

    if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: items[0] && items[0].id,
        cartData: response
      });
    }

    if (cartDrawer && cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
    if (cartNotification && cartNotification.classList.contains('is-empty')) cartNotification.classList.remove('is-empty');

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') cartDrawer.renderContents(response);
    if (cartItems && typeof cartItems.renderContents === 'function') cartItems.renderContents(response);
    if (cartNotification && typeof cartNotification.renderContents === 'function') cartNotification.renderContents(response);
    if (submitButton) submitButton.removeAttribute('aria-disabled');
  }

  function cartRequiresValidation() {
    if (state.cartValidationComplete) return false;
    return getDeviceValidationCartItems().some(function (item) {
      return !itemHasDeviceProperties(item);
    });
  }

  function onCartChanged() {
    state.liveCartItems = null;
  }

  function getDeviceValidationCartItems() {
    if (state.liveCartItems) return state.liveCartItems.slice();

    var items = [];
    var seen = {};
    document.querySelectorAll('[data-device-validation-cart]').forEach(function (script) {
      parseDeviceValidationCartScript(script).forEach(function (item) {
        var key = item.key || String(item.line);
        if (!key || seen[key]) return;
        seen[key] = true;
        items.push(item);
      });
    });
    return items;
  }

  function checkFreshCart(callback) {
    if (state.cartCheckInProgress) return;
    state.cartCheckInProgress = true;

    refreshDeviceValidationCartItems()
      .then(callback)
      .catch(function (err) {
        console.error('[DeviceValidation] cart check error:', err);
        alert('Unable to verify your cart. Please try again.');
      })
      .finally(function () {
        state.cartCheckInProgress = false;
      });
  }

  function refreshDeviceValidationCartItems() {
    if (state.cartRefreshPromise) return state.cartRefreshPromise;

    var cartUrl = (window.routes && routes.cart_url) || '/cart';
    var separator = cartUrl.indexOf('?') === -1 ? '?' : '&';
    var url = cartUrl + separator + 'section_id=device-validation-cart-state&_=' + Date.now();

    state.cartRefreshPromise = fetch(url, {
      method: 'GET',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart state request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var script = doc.querySelector('[data-device-validation-cart]');
        if (!script) throw new Error('Cart state data missing');
        state.liveCartItems = parseDeviceValidationCartScript(script);
        return state.liveCartItems;
      })
      .finally(function () {
        state.cartRefreshPromise = null;
      });

    return state.cartRefreshPromise;
  }

  function parseDeviceValidationCartScript(script) {
    var body = null;
    try {
      body = JSON.parse((script && script.textContent) || '{}');
    } catch (_) {
      body = null;
    }
    return body && Array.isArray(body.items) ? body.items : [];
  }

  function getDeviceValidationCartQuantity() {
    return getDeviceValidationCartItems().reduce(function (sum, item) {
      var quantity = parseInt(item && item.quantity, 10);
      return sum + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
    }, 0);
  }

  function cartHasDeviceValidationItem() {
    return getDeviceValidationCartQuantity() > 0;
  }

  function getBindableExistingCartItem(form) {
    var items = getDeviceValidationCartItems();
    if (items.length !== 1 || getDeviceValidationCartQuantity() !== 1) return null;

    var item = items[0];
    if (itemHasDeviceProperties(item)) return null;

    var variantId = getFormVariantId(form);
    if (!variantId || String(item.variantId) !== variantId) return null;
    return item;
  }

  function getFormVariantId(form) {
    if (!form) return '';
    var variantId = new FormData(form).get('id');
    return variantId == null ? '' : String(variantId);
  }

  function cartExceedsSingleDeviceValidationItem() {
    return getDeviceValidationCartQuantity() > 1;
  }

  function showSinglePurchaseMessage() {
    var toast = document.querySelector('[data-dvp-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'dvp-toast';
      toast.setAttribute('data-dvp-toast', '');
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      document.body.appendChild(toast);
    }

    toast.textContent = SINGLE_PURCHASE_MESSAGE;
    window.clearTimeout(state.singlePurchaseToastTimer);
    window.requestAnimationFrame(function () {
      toast.classList.add('is-active');
    });
    state.singlePurchaseToastTimer = window.setTimeout(function () {
      toast.classList.remove('is-active');
    }, 2600);
  }

  function itemHasDeviceProperties(item) {
    var properties = (item && item.properties) || {};
    return !!(properties[LIP_MAC] && properties[LIP_MODEL]);
  }

  function cartHasMac(normalizedMac) {
    if (!normalizedMac) return false;
    return getDeviceValidationCartItems().some(function (item) {
      if (!itemHasDeviceProperties(item)) return false;
      var properties = (item && item.properties) || {};
      return normalizeMac(properties[LIP_MAC]) === normalizedMac;
    });
  }

  function continueBindCartItem(pending, devices) {
    var itemKey = pending && pending.item && pending.item.key;
    if (!itemKey) return Promise.reject(new Error('Missing cart item'));

    return updateCartDeviceProperties(devices, itemKey).then(function () {
      window.location.href = (window.routes && routes.cart_url) || '/cart';
    });
  }

  function continueCheckout(pending, devices) {
    var form = pending && pending.form;
    if (!form) return Promise.resolve();

    return updateCartDeviceProperties(devices).then(function () {
      state.cartValidationComplete = true;
      state.checkoutProceeding = true;
      ensureCheckoutInput(form);
      HTMLFormElement.prototype.submit.call(form);
    });
  }

  function updateCartDeviceProperties(devices, itemKey) {
    if (!devices || !devices.length) return Promise.resolve();

    var items = getDeviceValidationCartItems()
      .filter(function (item) {
        if (itemHasDeviceProperties(item)) return false;
        return !itemKey || item.key === itemKey;
      })
      .sort(function (a, b) { return b.line - a.line; });

    if (itemKey && !items.length) return Promise.reject(new Error('Cart item changed'));

    var deviceIndex = 0;

    return items.reduce(function (promise, item) {
      return promise.then(function () {
        var device = devices[deviceIndex];
        deviceIndex += 1;
        if (!device) return Promise.resolve();

        var properties = Object.assign({}, item.properties || {});
        properties[LIP_MAC] = device.mac;
        properties[LIP_MODEL] = device.productName;

        var cartChangeUrl = (window.routes && routes.cart_change_url) || '/cart/change.js';
        return fetchJson(cartChangeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            line: item.line,
            quantity: item.quantity,
            properties: properties
          })
        }, DEVICE_TIMEOUT_MS);
      });
    }, Promise.resolve());
  }

  function ensureCheckoutInput(form) {
    var checkoutInput = form.querySelector('input[type="hidden"][name="checkout"]');
    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      form.appendChild(checkoutInput);
    }
    checkoutInput.value = 'Checkout';
  }

  // ------------------------------------------------------------------
  // BACKEND CALLS
  // ------------------------------------------------------------------
  function loadProductModels() {
    var businessParams = {
      pageNo: '1',
      pageSize: '100'
    };
    if (PROJECT_ID) businessParams.projectId = PROJECT_ID;

    return generateSignature(businessParams).then(function (signed) {
      var url = API_BASE + '/api/robot/rtk/product/list';
      var query = new URLSearchParams();
      Object.keys(businessParams).forEach(function (k) {
        query.set(k, businessParams[k]);
      });

      return fetchJson(url + '?' + query.toString(), {
        method: 'GET',
        headers: {
          appId: APP_ID,
          signature: signed.signature,
          timestamp: String(signed.timestamp),
          nonce: signed.nonce
        }
      }, DEVICE_TIMEOUT_MS)
        .then(function (body) {
          if (body && body.code === '0' && Array.isArray(body.data)) return body.data;
          throw new Error((body && body.msg) || 'Failed to load product list');
        });
    });
  }

  function validateDevice(deviceId, productKey) {
    var businessParams = {
      deviceId: deviceId,
      productKey: productKey
    };
    if (PROJECT_ID) businessParams.projectId = PROJECT_ID;

    return generateSignature(businessParams).then(function (signed) {
      var url = API_BASE + '/api/robot/rtk/getDeviceModel';
      var query = new URLSearchParams();
      Object.keys(businessParams).forEach(function (k) {
        query.set(k, businessParams[k]);
      });

      return fetchJson(url + '?' + query.toString(), {
        method: 'GET',
        headers: {
          appId: APP_ID,
          signature: signed.signature,
          timestamp: String(signed.timestamp),
          nonce: signed.nonce
        }
      }, DEVICE_TIMEOUT_MS)
        .then(function (body) {
          if (body && body.code === '0' && body.data) {
            if (body.data.status === 0) {
              return { ok: false, message: 'Device found but not activated yet.' };
            }
            return { ok: true, data: body.data };
          }
          return { ok: false, message: humanizeError(body) };
        });
    });
  }

  function generateSignature(businessParams) {
    if (!PRIVATE_KEY) {
      return Promise.reject(new Error('privateKey missing in DeviceValidationConfig'));
    }
    return requestJsonWhenReady(API_BASE + '/api/sign/generateSignature', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        appId: APP_ID,
        privateKey: PRIVATE_KEY
      },
      body: JSON.stringify({ businessParams: businessParams })
    }, SIGNATURE_TIMEOUT_MS)
      .then(function (body) {
        if (body && body.code === '0' && body.data) return body.data;
        throw new Error((body && body.msg) || 'Failed to sign request');
      });
  }

  function humanizeError(body) {
    if (!body) return 'Device not found.';
    if (body.code === 'T70030') return 'The device MAC does not exist.';
    return body.msg || 'Device not found.';
  }

  function fetchJson(url, options, timeoutMs) {
    var controller = window.AbortController ? new AbortController() : null;
    var timer = null;
    options = options || {};

    if (controller) {
      options.signal = controller.signal;
      timer = setTimeout(function () {
        controller.abort();
      }, timeoutMs);
    }

    return fetch(url, options)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  function requestJsonWhenReady(url, options, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      var done = false;

      function finish(fn, value) {
        if (done) return;
        done = true;
        try { xhr.abort(); } catch (_) { /* noop */ }
        fn(value);
      }

      function tryResolveFromText() {
        if (!xhr.responseText) return;
        try {
          finish(resolve, JSON.parse(xhr.responseText));
        } catch (_) { /* response is not complete yet */ }
      }

      xhr.open((options && options.method) || 'GET', url, true);
      xhr.timeout = timeoutMs;

      var headers = (options && options.headers) || {};
      Object.keys(headers).forEach(function (name) {
        xhr.setRequestHeader(name, headers[name]);
      });

      xhr.onprogress = tryResolveFromText;
      xhr.onreadystatechange = function () {
        if (xhr.readyState === 3 || xhr.readyState === 4) tryResolveFromText();
      };
      xhr.onload = function () {
        if (done) return;
        tryResolveFromText();
        if (!done) finish(reject, new Error('Invalid JSON response'));
      };
      xhr.onerror = function () {
        finish(reject, new Error('Network error'));
      };
      xhr.ontimeout = function () {
        tryResolveFromText();
        if (!done) finish(reject, new Error('Request timeout'));
      };

      xhr.send(options && options.body ? options.body : null);
    });
  }

  // ------------------------------------------------------------------
  // UI HELPERS
  // ------------------------------------------------------------------
  function getPendingDeviceCount(pending) {
    if (!pending) return 1;
    if (pending.kind === 'add-to-cart') {
      return getSinglePurchaseQuantity(getFormQuantity(pending.element, pending.submitter));
    }
    if (pending.kind === 'buy-now') {
      return getSinglePurchaseQuantity(getFormQuantity(pending.form, pending.submitter || pending.element));
    }
    if (pending.kind === 'checkout') {
      var missingItems = getDeviceValidationCartItems().filter(function (item) {
        return !itemHasDeviceProperties(item);
      });
      return Math.max(missingItems.length, 1);
    }
    return 1;
  }

  function getSinglePurchaseQuantity(quantity) {
    return Math.min(Math.max(parseInt(quantity, 10) || 1, 1), 1);
  }

  function getFormQuantity(form, trigger) {
    if (!form) {
      var triggerInput = getContractQuantityInput(trigger) || getQuantityInputNearTrigger(trigger);
      var triggerQuantity = parseInt(triggerInput && triggerInput.value, 10);
      return Number.isFinite(triggerQuantity) && triggerQuantity > 0 ? triggerQuantity : 1;
    }

    var input = getQuantityInput(form, trigger);
    var quantity = parseInt(input && input.value, 10);
    var formDataQuantity = parseInt(new FormData(form).get('quantity'), 10);

    if (Number.isFinite(quantity) && quantity > 0) return quantity;

    quantity = formDataQuantity;
    if (Number.isFinite(quantity) && quantity > 0) return quantity;

    if (!Number.isFinite(quantity) || quantity < 1) return 1;
    return quantity;
  }

  function getQuantityInput(form, trigger) {
    if (!form) return null;

    var input = getContractQuantityInput(trigger);
    if (input) return input;

    input = getContractQuantityInput(form);
    if (input) return input;

    input = getQuantityInputNearTrigger(trigger);
    if (input) return input;

    var input = getVisibleProductQuantityInput(form);
    if (input) return input;

    input = form.querySelector('[name="quantity"]');
    if (input) return input;

    if (form.elements && form.elements.quantity) return form.elements.quantity;

    if (form.id) {
      input = document.querySelector('[form="' + cssEscape(form.id) + '"][name="quantity"]');
      if (input) return input;
    }

    if (SECTION_ID) {
      input = document.getElementById('Quantity-' + SECTION_ID + '-inline') ||
        document.getElementById('Quantity-' + SECTION_ID);
      if (input) return input;
    }

    var mainProduct = MAIN_PRODUCT_ID ? document.getElementById(MAIN_PRODUCT_ID) : null;
    if (mainProduct) {
      input = mainProduct.querySelector('.quantity__input[name="quantity"], .quantity__input');
      if (input) return input;
    }

    return null;
  }

  function getQuantityInputNearTrigger(trigger) {
    var source = trigger || document.activeElement;
    if (!source || !source.closest) return null;

    var containers = [
      source.closest('.product-form__buttons'),
      source.closest('.flex'),
      source.closest('product-form')
    ];

    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      if (!container) continue;
      var input = container.querySelector('input.quantity__input[name="quantity"], input.quantity__input[id^="Quantity-"]');
      if (input && isVisibleInput(input)) return input;
    }

    return null;
  }

  function getContractQuantityInput(element) {
    if (!element || !element.getAttribute) return null;

    var quantityId = element.getAttribute('data-device-validation-quantity');
    if (quantityId) {
      var input = document.getElementById(quantityId);
      if (input) return input;
    }

    var selector = element.getAttribute('data-device-validation-quantity-selector');
    if (selector) {
      try {
        return document.querySelector(selector);
      } catch (_) {
        return null;
      }
    }

    return null;
  }

  function getVisibleProductQuantityInput(form) {
    var candidates = [];

    if (form.id) {
      candidates = candidates.concat(Array.prototype.slice.call(
        document.querySelectorAll('[form="' + cssEscape(form.id) + '"][name="quantity"]')
      ));
    }

    if (SECTION_ID) {
      [
        document.getElementById('Quantity-' + SECTION_ID + '-inline'),
        document.getElementById('Quantity-' + SECTION_ID)
      ].forEach(function (input) {
        if (input) candidates.push(input);
      });
    }

    var mainProduct = MAIN_PRODUCT_ID ? document.getElementById(MAIN_PRODUCT_ID) : null;
    if (mainProduct) {
      candidates = candidates.concat(Array.prototype.slice.call(
        mainProduct.querySelectorAll('input.quantity__input[name="quantity"], input.quantity__input[id^="Quantity-"]')
      ));
    }

    candidates = candidates.concat(Array.prototype.slice.call(
      document.querySelectorAll('main input.quantity__input[name="quantity"], main input.quantity__input[id^="Quantity-"]')
    ));

    var seen = [];
    var visible = candidates.filter(function (input) {
      if (!input || seen.indexOf(input) !== -1) return false;
      seen.push(input);
      if (input.closest && input.closest('cart-drawer, cart-notification, .quick-add-modal, .section-main-cart')) return false;
      return isVisibleInput(input);
    });

    var inline = visible.filter(function (input) {
      return /-inline$/.test(input.id || '');
    });

    return inline[0] || visible[0] || null;
  }

  function isVisibleInput(input) {
    if (!input) return false;
    var rect = input.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && window.getComputedStyle(input).visibility !== 'hidden';
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  }

  function renderDeviceRows(count) {
    if (!state.devicesEl) return;

    count = Math.max(parseInt(count, 10) || 1, 1);
    state.devicesEl.innerHTML = '';
    state.deviceRows = [];

    for (var i = 0; i < count; i++) {
      var row = createDeviceRow(i, count);
      state.devicesEl.appendChild(row.el);
      state.deviceRows.push(row);
    }

    populateModelOptions(state.products);
    updateCountText(count);
    updateRowStates();
  }

  function createDeviceRow(index, totalCount) {
    var rowEl = document.createElement('div');
    rowEl.className = 'dvp-device';
    rowEl.dataset.dvpDevice = String(index + 1);

    var rowState = null;

    if (totalCount > 1) {
      var title = document.createElement('div');
      title.className = 'dvp-device-title';

      var label = document.createElement('span');
      label.textContent = 'Device ' + (index + 1);

      rowState = document.createElement('span');
      rowState.className = 'dvp-device-state';
      rowState.textContent = '';

      title.appendChild(label);
      title.appendChild(rowState);
      rowEl.appendChild(title);
    } else {
      rowEl.classList.add('dvp-device--single');
    }

    var fields = document.createElement('div');
    fields.className = 'dvp-device-fields';
    rowEl.appendChild(fields);

    var macId = 'DeviceMacInput-' + (index + 1);
    var modelId = 'DeviceModelSelect-' + (index + 1);

    var macField = document.createElement('div');
    macField.className = 'dvp-field';
    macField.innerHTML =
      '<div class="dvp-control-row">' +
        '<div class="dvp-input-wrap">' +
          '<input id="' + macId + '" type="text" class="dvp-input" data-dvp-mac placeholder=" " autocomplete="off" spellcheck="false" required>' +
          '<label for="' + macId + '" class="dvp-floating-label">Device MAC</label>' +
        '</div>' +
      '</div>';

    var modelField = document.createElement('div');
    modelField.className = 'dvp-field';
    modelField.innerHTML =
      '<div class="dvp-control-row">' +
        '<div class="dvp-input-wrap">' +
          '<select id="' + modelId + '" class="dvp-select" data-dvp-model required></select>' +
          '<label for="' + modelId + '" class="dvp-floating-label">Device Model</label>' +
        '</div>' +
      '</div>';

    fields.appendChild(macField);
    fields.appendChild(modelField);
    var errorEl = document.createElement('div');
    errorEl.className = 'dvp-device-error';
    errorEl.setAttribute('role', 'alert');
    fields.appendChild(errorEl);

    return {
      el: rowEl,
      stateEl: rowState,
      macInput: macField.querySelector('[data-dvp-mac]'),
      modelSelect: modelField.querySelector('[data-dvp-model]'),
      errorEl: errorEl
    };
  }

  function updateCountText(count) {
    if (!state.countText) return;
    if (count > 1) {
      state.countText.textContent = 'You are adding ' + count + ' 4G services. Each service must be linked to one RoboUP device.';
    } else {
      state.countText.textContent = 'Each 4G service must be linked to one RoboUP device.';
    }
  }

  function updateRowStates() {
    state.deviceRows.forEach(function (row) {
      var complete = !!((row.macInput.value || '').trim() && row.modelSelect.value);
      row.el.classList.toggle('is-complete', complete && !row.el.classList.contains('has-error'));
      if (row.stateEl && !row.el.classList.contains('has-error')) {
        row.stateEl.textContent = complete ? 'Verified' : '';
      }
    });
  }

  function showRowError(row, message) {
    showRowErrors([{ row: row, message: message }]);
  }

  function showRowErrors(errors) {
    errors = (errors || []).filter(function (error) {
      return error && error.row;
    });

    if (!errors.length) {
      setStatus('Please check your device information.', 'error');
      return;
    }

    clearStatus();
    clearRowErrors();
    errors.forEach(function (error) {
      markRowError(error.row, error.message);
    });
  }

  function markRowError(row, message) {
    if (!row) {
      setStatus(message, 'error');
      return;
    }
    row.el.classList.add('has-error');
    row.el.classList.remove('is-complete');
    if (row.stateEl) row.stateEl.textContent = 'Needs attention';
    row.errorEl.textContent = message || 'Please check this device.';
  }

  function focusFirstError(errors) {
    if (!errors || !errors.length) return;
    var first = errors[0];
    var focusEl = first.focusEl || (first.row && first.row.macInput);
    if (focusEl && typeof focusEl.focus === 'function') focusEl.focus();
  }

  function clearRowErrors() {
    state.deviceRows.forEach(function (row) {
      row.el.classList.remove('has-error');
      row.errorEl.textContent = '';
    });
    updateRowStates();
  }

  function populateModelOptions(products) {
    state.deviceRows.forEach(function (row) {
      populateSelectOptions(row.modelSelect, products);
    });
  }

  function populateSelectOptions(select, products) {
    if (!select) return;

    var currentValue = select.value;
    clearModelOptions(select);
    (products || [])
      .filter(function (product) {
        return product && product.productKey && product.productName;
      })
      .forEach(function (product) {
        var option = document.createElement('option');
        option.value = product.productKey;
        option.textContent = product.productName;
        option.dataset.productModel = product.productModel || '';
        option.dataset.projectId = product.projectId || '';
        select.appendChild(option);
      });

    if (currentValue) selectModelOption(select, currentValue);
    select.disabled = select.options.length <= 1;
    if (select.disabled) {
      addModelStatusOption(select, 'No device models available.');
    }
  }

  function clearModelOptions(select) {
    if (!select) return;
    select.innerHTML = '';
    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.disabled = true;
    placeholder.selected = true;
    placeholder.hidden = true;
    select.appendChild(placeholder);
  }

  function setModelSelectLoading(on) {
    if (!on) return;
    state.deviceRows.forEach(function (row) {
      clearModelOptions(row.modelSelect);
      row.modelSelect.disabled = true;
      addModelStatusOption(row.modelSelect, 'Loading device models...');
    });
  }

  function setModelSelectError() {
    state.deviceRows.forEach(function (row) {
      clearModelOptions(row.modelSelect);
      row.modelSelect.disabled = true;
      addModelStatusOption(row.modelSelect, 'Unable to load device models.');
    });
  }

  function addModelStatusOption(select, text) {
    if (!select) return;
    var option = document.createElement('option');
    option.value = '';
    option.disabled = true;
    option.selected = true;
    option.textContent = text;
    select.appendChild(option);
  }

  function selectModelOption(select, productKey) {
    if (!select || !productKey) return false;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === productKey) {
        select.value = productKey;
        return true;
      }
    }
    return false;
  }

  function setLoading(on) {
    if (!state.submitBtn) return;
    state.submitBtn.disabled = !!on;
    state.submitBtn.classList.toggle('is-loading', !!on);
    if (on) {
      setSuccessState(false);
      setSubmitText('Submit');
      setStatus('Checking device...', 'success');
    } else if (!state.submitBtn.classList.contains('is-success')) {
      setSubmitText('Submit');
    }
  }

  function setSuccessState(on) {
    if (!state.submitBtn) return;
    state.submitBtn.classList.toggle('is-success', !!on);
    state.submitBtn.disabled = !!on;
    if (on) setSubmitText('Verified');
    else setSubmitText('Submit');
  }

  function setSubmitText(text) {
    if (!state.submitText) return;
    state.submitText.textContent = text;
  }

  function setStatus(msg, kind) {
    if (!state.statusEl) return;
    state.statusEl.textContent = msg || '';
    state.statusEl.classList.remove('is-success', 'is-error');
    if (kind) state.statusEl.classList.add('is-' + kind);
  }

  function clearStatus() { setStatus('', null); }

  function normalizeMac(s) {
    return String(s || '').replace(/[\s:\-]/g, '').toLowerCase();
  }

  // ------------------------------------------------------------------
  // START
  // ------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
